import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import HttpRoutingService from "../../shared/services/httpRoutingService";
import { API } from "../../shared/apiRoutes";
import { config } from "../../shared/environment";
import { DashboardDetailsResponse, PosStoreSetupResponse, RevenueAnalyticsResponse, StoreDataResponse } from "../../components/dashboard/dashboard.module";
import { RootState } from "../store";

interface DashboardCacheItem {
    dashboardSummary?: DashboardDetailsResponse;
    revenueAnalytics?: RevenueAnalyticsResponse;
}

type CommonType =
    {
        loading: boolean,
        error: string
    }

interface IntialState {
    storeSetupStatus: CommonType & { value: PosStoreSetupResponse | null },
    revenueAnalytics: CommonType & { value: RevenueAnalyticsResponse | null },
    dashboardSummary: CommonType & { value: DashboardDetailsResponse | null },
    storeData: CommonType & { value: StoreDataResponse | null },
    currentFilter: string | null;
    dashboardCache: Record<string, DashboardCacheItem>;
}

const initialState: IntialState = {
    storeSetupStatus: { error: '', loading: false, value: null },
    revenueAnalytics: { error: '', loading: false, value: null },
    dashboardSummary: { error: '', loading: false, value: null },
    storeData: { error: '', loading: false, value: null },
    currentFilter: null,
    dashboardCache: {}
}


export const getStoreSetupStatus = createAsyncThunk('dashboard/storeSetupStatus', () => {
    let Url = HttpRoutingService.replaceUrl(API.StoreSetupReport, { storeId: config.storeId })
    return HttpRoutingService.getMethod<PosStoreSetupResponse>(Url, undefined)
})

export const getStoreRevenueAnalytics = createAsyncThunk('dashboard/revenueAnaytics', async ({ data, forceRefresh }: { data: { filterData: string }, forceRefresh?: boolean, [key: string]: any }, { getState }) => {
    const state = getState() as RootState;
    const filterKey = data.filterData || 'default';

    if (!forceRefresh && state.Dashboard.dashboardCache[filterKey]?.revenueAnalytics) {
        return state.Dashboard.dashboardCache[filterKey].revenueAnalytics!;
    }
    let Url = HttpRoutingService.replaceUrl(API.RevenueAnayticsReport, { storeId: config.storeId })
    return await HttpRoutingService.getMethod<RevenueAnalyticsResponse>(Url, data)
})

export const getDashboardSummary = createAsyncThunk('dashboard/dashboardSummary', async ({ data, forceRefresh }: { data: { filterData: string }, forceRefresh?: boolean, [key: string]: any }, { getState }) => {
    const state = getState() as RootState;
    const filterKey = data.filterData || 'default';

    if (!forceRefresh && state.Dashboard.dashboardCache[filterKey]?.dashboardSummary) {
        return state.Dashboard.dashboardCache[filterKey].dashboardSummary!;
    }
    let Url = HttpRoutingService.replaceUrl(API.DashboardSummary, { storeId: config.storeId })
    return await HttpRoutingService.getMethod<DashboardDetailsResponse>(Url, data)
})

export const getStoreDataReport = createAsyncThunk('dashboard/storeData', () => {
    let Url = HttpRoutingService.replaceUrl(API.DashboardReport, { storeId: config.storeId })
    return HttpRoutingService.getMethod<StoreDataResponse>(Url, undefined, "v1/")
})

export const DashboardSlice = createSlice({
    initialState,
    name: 'dashboard',
    reducers: {
        setDashboardFilter: (state, action: PayloadAction<string>) => {
            state.currentFilter = action.payload
        },
    },
    extraReducers(builder) {
        builder.addCase(getStoreSetupStatus?.pending, (state) => {
            state.storeSetupStatus.loading = true
        })
        builder.addCase(getStoreSetupStatus?.fulfilled, (state, action: PayloadAction<PosStoreSetupResponse>) => {
            state.storeSetupStatus.loading = false
            state.storeSetupStatus.value = action?.payload
        })
        builder.addCase(getStoreSetupStatus?.rejected, (state, action) => {
            state.storeSetupStatus.loading = false
            state.storeSetupStatus.error = action?.error?.message ? action?.error?.message : 'Error in getStoreSetupStatus'
        })
        builder.addCase(getStoreRevenueAnalytics?.pending, (state) => {
            state.revenueAnalytics.loading = true
        })
        builder.addCase(getStoreRevenueAnalytics?.fulfilled, (state, action) => {
            state.revenueAnalytics.loading = false
            state.revenueAnalytics.value = action?.payload
            const filterKey = action.meta.arg.data.filterData || 'default';
            if (!state.dashboardCache[filterKey]) {
                state.dashboardCache[filterKey] = {};
            }
            state.dashboardCache[filterKey].revenueAnalytics = action.payload;
        })
        builder.addCase(getStoreRevenueAnalytics?.rejected, (state, action) => {
            state.revenueAnalytics.loading = false
            state.revenueAnalytics.error = action?.error?.message ? action?.error?.message : 'Error in getStoreRevenueAnalytics'
        })
        builder.addCase(getDashboardSummary?.pending, (state) => {
            state.dashboardSummary.loading = true
        })
        builder.addCase(getDashboardSummary?.fulfilled, (state, action) => {
            state.dashboardSummary.loading = false
            state.dashboardSummary.value = action?.payload
            const filterKey = action.meta.arg.data.filterData || 'default';
            if (!state.dashboardCache[filterKey]) {
                state.dashboardCache[filterKey] = {};
            }
            state.dashboardCache[filterKey].dashboardSummary = action.payload;
        })
        builder.addCase(getDashboardSummary?.rejected, (state, action) => {
            state.dashboardSummary.loading = false
            state.dashboardSummary.error = action?.error?.message ? action?.error?.message : 'Error in getDashboardSummary'
        })
        builder.addCase(getStoreDataReport?.pending, (state) => {
            state.storeData.loading = true
        })
        builder.addCase(getStoreDataReport?.fulfilled, (state, action: PayloadAction<StoreDataResponse>) => {
            state.storeData.loading = false
            state.storeData.value = action?.payload
        })
        builder.addCase(getStoreDataReport?.rejected, (state, action) => {
            state.storeData.loading = false
            state.storeData.error = action?.error?.message ? action?.error?.message : 'Error in getStoreDataReport'
        })
    },
})


export const { setDashboardFilter } = DashboardSlice.actions
export default DashboardSlice?.reducer